import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AdminLogin } from '../components/AdminLogin';
import { ADMIN_PATH } from '../config/adminPath';
import { 
  Users, 
  DollarSign, 
  Settings, 
  Mail, 
  Phone, 
  Building, 
  Eye,
  Edit,
  Save,
  RefreshCw,
  LogOut,
  BarChart3,
  Globe,
  MapPin,
  Wifi,
  TrendingUp,
  Clock,
  Calendar,
  Activity,
  PieChart,
  Download,
  Upload,
  Database
} from 'lucide-react';

export const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [leads, setLeads] = useState([]);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contentData, setContentData] = useState({
    hero: {
      headline: '',
      subheadline: '',
      description: '',
      ctaPrimary: ''
    },
    pricing: {
      regularPrice: 3000,
      promoPrice: 2100,
      setupRegular: 3000,
      setupPromo: 2000
    },
    countdown: {
      expirationDate: ''
    },
    slots: {
      totalSlots: 10,
      availableSlots: 7
    }
  });

  const [pricingSettings, setPricingSettings] = useState({
    starter_regular: 300,
    starter_promo: 250,
    essencial_regular: 350,
    essencial_promo: 290,
    professional_regular: 400,
    professional_promo: 330,
    business_regular: 0,
    business_promo: 0,
    extra_bay_regular: 100,
    extra_bay_promo: 80,
    setup_regular: 3000,
    setup_promo: 1650,
    rosie_implementation_regular: 2000,
    rosie_implementation_promo: 1300,
    rosie_monthly_regular: 2000,
    rosie_monthly_promo: 1300,
    discount_percentage: 45
  });

  const [institutionalSettings, setInstitutionalSettings] = useState({
    is_active: false,
    whatsapp_number: '5513996265777',
    base_price: 99
  });
  
  const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    // Check if user is already authenticated
    const authStatus = localStorage.getItem('bobapet_admin_auth');
    if (authStatus === 'authenticated') {
      setIsAuthenticated(true);
      fetchLeads();
      fetchContent();
      fetchVisits();
      fetchPricingSettings();
      fetchInstitutionalSettings();
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchLeads();
      fetchContent();
      fetchVisits();
      fetchPricingSettings();
      fetchInstitutionalSettings();
    }
  }, [isAuthenticated]);

  const fetchLeads = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/leads`);
      if (response.ok) {
        const data = await response.json();
        const rows = Array.isArray(data) ? data : [];
        setLeads(
          rows.map((l) => ({
            ...l,
            timestamp: l.timestamp ?? l.created_at,
          })),
        );
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVisits = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/visits`);
      if (response.ok) {
        const data = await response.json();
        const rows = Array.isArray(data) ? data : [];
        // D1 usa created_at; Mongo legado usava timestamp
        setVisits(
          rows.map((v) => ({
            ...v,
            timestamp: v.timestamp ?? v.created_at,
          })),
        );
      }
    } catch (error) {
      console.error('Error fetching visits:', error);
    }
  };

  const fetchPricingSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/pricing-settings`);
      if (response.ok) {
        const data = await response.json();
        setPricingSettings(data);
      }
    } catch (error) {
      console.error('Error fetching pricing settings:', error);
    }
  };

  const savePricingSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/pricing-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(pricingSettings),
      });
      
      if (response.ok) {
        alert('Preços atualizados com sucesso!');
        fetchPricingSettings();
      }
    } catch (error) {
      console.error('Error saving pricing settings:', error);
      alert('Erro ao salvar preços. Tente novamente.');
    }
  };

  const fetchInstitutionalSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/institutional/settings`);
      if (response.ok) {
        const data = await response.json();
        setInstitutionalSettings(data);
      }
    } catch (error) {
      console.error('Error fetching Institutional settings:', error);
    }
  };

  const saveInstitutionalSettings = async () => {
    try {
      const updatePayload = {
        is_active: institutionalSettings.is_active,
        whatsapp_number: institutionalSettings.whatsapp_number,
        base_price: institutionalSettings.base_price,
      };
      
      const response = await fetch(`${BACKEND_URL}/api/institutional/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatePayload),
      });
      
      if (response.ok) {
        alert('🏢 Configurações do Site Institucional atualizadas com sucesso!');
        fetchInstitutionalSettings();
      } else {
        const errorData = await response.text();
        console.error('Error response:', errorData);
        alert('Erro ao salvar configurações. Tente novamente.');
      }
    } catch (error) {
      console.error('Error saving Institutional settings:', error);
      alert('Erro ao salvar configurações do Site Institucional. Tente novamente.');
    }
  };

  const downloadBackup = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/backup/export`);
      if (response.ok) {
        const backupData = await response.json();
        const dataStr = JSON.stringify(backupData, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `bobapet-backup-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        alert('Backup baixado com sucesso!');
      }
    } catch (error) {
      console.error('Error downloading backup:', error);
      alert('Erro ao baixar backup. Tente novamente.');
    }
  };

  const restoreBackup = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const backupData = JSON.parse(text);
      
      const confirmRestore = window.confirm(
        `⚠️ ATENÇÃO: Isso vai SUBSTITUIR todos os dados atuais!\n\n` +
        `Leads: ${backupData.leads?.length || 0}\n` +
        `Visitas: ${backupData.visits?.length || 0}\n` +
        `\nDeseja continuar?`
      );
      
      if (!confirmRestore) return;

      const response = await fetch(`${BACKEND_URL}/api/backup/restore`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(backupData),
      });
      
      if (response.ok) {
        const result = await response.json();
        alert(`Backup restaurado com sucesso!\n\n${JSON.stringify(result.restored, null, 2)}`);
        // Refresh all data
        fetchLeads();
        fetchVisits();
        fetchPricingSettings();
        fetchInstitutionalSettings();
        fetchContent();
      }
    } catch (error) {
      console.error('Error restoring backup:', error);
      alert('Erro ao restaurar backup. Verifique o arquivo e tente novamente.');
    }
  };

  const fetchContent = async () => {
    try {
      const sections = ['hero', 'pricing'];
      for (const section of sections) {
        const response = await fetch(`${BACKEND_URL}/api/content/${section}`);
        if (response.ok) {
          const data = await response.json();
          if (data.content && Object.keys(data.content).length > 0) {
            setContentData(prev => ({
              ...prev,
              [section]: data.content
            }));
          }
        }
      }
      
      // Fetch countdown separately
      const countdownResponse = await fetch(`${BACKEND_URL}/api/promo-countdown`);
      if (countdownResponse.ok) {
        const countdownData = await countdownResponse.json();
        const date = new Date(countdownData.expiration_date);
        const localDateString = date.toISOString().slice(0, 16);
        setContentData(prev => ({
          ...prev,
          countdown: { expirationDate: localDateString }
        }));
      }
      
      // Fetch slots separately
      const slotsResponse = await fetch(`${BACKEND_URL}/api/promo-slots`);
      if (slotsResponse.ok) {
        const slotsData = await slotsResponse.json();
        setContentData(prev => ({
          ...prev,
          slots: { 
            totalSlots: slotsData.total_slots,
            availableSlots: slotsData.available_slots 
          }
        }));
      }
    } catch (error) {
      console.error('Error fetching content:', error);
    }
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/leads/${leadId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });
      
      if (response.ok) {
        fetchLeads(); // Refresh leads
      }
    } catch (error) {
      console.error('Error updating lead status:', error);
    }
  };

  const saveContent = async (section) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/content`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          section,
          content: contentData[section]
        }),
      });
      
      if (response.ok) {
        alert(`Seção ${section} atualizada com sucesso!`);
      }
    } catch (error) {
      console.error('Error saving content:', error);
      alert('Erro ao salvar. Tente novamente.');
    }
  };

  const saveCountdown = async () => {
    try {
      if (!contentData.countdown.expirationDate) {
        alert('Por favor, selecione uma data de expiração.');
        return;
      }

      const expirationDate = new Date(contentData.countdown.expirationDate).toISOString();
      
      const response = await fetch(`${BACKEND_URL}/api/promo-countdown`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          expiration_date: expirationDate
        }),
      });
      
      if (response.ok) {
        alert('Timer de promoção atualizado com sucesso!');
        fetchContent(); // Refresh data
      }
    } catch (error) {
      console.error('Error saving countdown:', error);
      alert('Erro ao salvar timer. Tente novamente.');
    }
  };

  const saveSlots = async () => {
    try {
      if (!contentData.slots.totalSlots || contentData.slots.totalSlots < 0) {
        alert('Por favor, insira um número válido para o total de vagas.');
        return;
      }
      
      if (!contentData.slots.availableSlots || contentData.slots.availableSlots < 0) {
        alert('Por favor, insira um número válido de vagas disponíveis.');
        return;
      }
      
      if (contentData.slots.availableSlots > contentData.slots.totalSlots) {
        alert('Vagas disponíveis não podem ser maiores que o total de vagas!');
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/promo-slots`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          total_slots: parseInt(contentData.slots.totalSlots),
          available_slots: parseInt(contentData.slots.availableSlots)
        }),
      });
      
      if (response.ok) {
        alert('Vagas atualizadas com sucesso!');
        fetchContent(); // Refresh data
      }
    } catch (error) {
      console.error('Error saving slots:', error);
      alert('Erro ao salvar vagas. Tente novamente.');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      novo: 'bg-blue-100 text-blue-800',
      contatado: 'bg-yellow-100 text-yellow-800',
      demo_agendada: 'bg-orange-100 text-orange-800',
      fechado: 'bg-green-100 text-green-800',
      negado: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const d = new Date(dateString);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("pt-BR");
  };

  const handleLogin = (success) => {
    if (success) {
      setIsAuthenticated(true);
      setLoading(true);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('bobapet_admin_auth');
    setIsAuthenticated(false);
    setLeads([]);
    setVisits([]);
    setContentData({
      hero: { headline: '', subheadline: '', description: '', ctaPrimary: '' },
      pricing: { regularPrice: 3000, promoPrice: 2100, setupRegular: 3000, setupPromo: 2000 },
      countdown: { expirationDate: '' },
      slots: { totalSlots: 10, availableSlots: 7 }
    });
  };

  // Analytics functions
  const getTopItems = (key, limit = 5) => {
    const counts = {};
    visits.forEach(visit => {
      const value = visit[key];
      if (value && value !== 'N/A') {
        counts[value] = (counts[value] || 0) + 1;
      }
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, limit)
      .map(([name, count]) => ({ name, count }));
  };

  const visitInstant = (visit) => visit.timestamp || visit.created_at;

  const getHourlyDistribution = () => {
    const hours = Array(24).fill(0);
    visits.forEach(visit => {
      const ts = visitInstant(visit);
      if (!ts) return;
      const date = new Date(ts);
      if (Number.isNaN(date.getTime())) return;
      const hour = date.getHours();
      hours[hour]++;
    });
    return hours.map((count, hour) => ({ 
      hour: `${hour}:00`, 
      count,
      label: hour < 10 ? `0${hour}:00` : `${hour}:00`
    }));
  };

  const getTopHours = () => {
    const hourly = getHourlyDistribution();
    return hourly
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  };

  const getDayOfWeekDistribution = () => {
    const days = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
    const counts = Array(7).fill(0);
    visits.forEach(visit => {
      const ts = visitInstant(visit);
      if (!ts) return;
      const date = new Date(ts);
      if (Number.isNaN(date.getTime())) return;
      const day = date.getDay();
      counts[day]++;
    });
    return counts.map((count, index) => ({ day: days[index], count }));
  };

  const getActiveUsers = () => {
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    return visits.filter((visit) => {
      const ts = visitInstant(visit);
      if (!ts) return false;
      const d = new Date(ts);
      return !Number.isNaN(d.getTime()) && d > fiveMinutesAgo;
    }).length;
  };

  const getConversionRate = () => {
    if (visits.length === 0) return 0;
    return ((leads.length / visits.length) * 100).toFixed(2);
  };

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p>Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {!BACKEND_URL ? (
        <div className="border-b border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          <strong className="font-semibold">API sem base URL.</strong> Os pedidos vão para{" "}
          <code className="rounded bg-amber-100/80 px-1.5 py-0.5 text-xs">
            {typeof window !== "undefined" ? window.location.origin : ""}
          </code>
          /api — no painel na Cloudflare não há Worker; em localhost o proxy manda para o FastAPI (Mongo), não para o
          D1. Para ver dados do site Astro, define{" "}
          <code className="rounded bg-amber-100/80 px-1.5 py-0.5 text-xs">
            REACT_APP_BACKEND_URL=https://www.bobapet.com.br
          </code>{" "}
          no <code className="rounded bg-amber-100/80 px-1.5 py-0.5 text-xs">.env</code> e reinicia o{" "}
          <code className="rounded bg-amber-100/80 px-1.5 py-0.5 text-xs">npm start</code> (ou volta a fazer{" "}
          <code className="rounded bg-amber-100/80 px-1.5 py-0.5 text-xs">npm run deploy</code>).
        </div>
      ) : null}
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_smart-pet-shop/artifacts/mulxtf9r_bobapetcombr.png" 
                alt="Bobapet Logo"
                className="w-10 h-10 object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
                <p className="text-gray-600">Bobapet.com.br + 7Rosie</p>
              </div>
            </div>
            <div className="flex space-x-3">
              <Button 
                variant="outline"
                onClick={() => window.location.href = '/'}
              >
                <Eye className="w-4 h-4 mr-2" />
                Ver Landing Page
              </Button>
              <Button 
                variant="destructive"
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sair
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="leads" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 lg:grid-cols-7">
            <TabsTrigger value="leads">
              <Users className="w-4 h-4 mr-2" />
              Leads ({leads.length})
            </TabsTrigger>
            <TabsTrigger value="analytics">
              <BarChart3 className="w-4 h-4 mr-2" />
              Visitas ({visits.length})
            </TabsTrigger>
            <TabsTrigger value="insights">
              <TrendingUp className="w-4 h-4 mr-2" />
              Insights
            </TabsTrigger>
            <TabsTrigger value="institutional">
              <span className="mr-2">🏢</span>
              Site Institucional
            </TabsTrigger>
            <TabsTrigger value="pricing">
              <DollarSign className="w-4 h-4 mr-2" />
              Preços
            </TabsTrigger>
            <TabsTrigger value="content">
              <Edit className="w-4 h-4 mr-2" />
              Conteúdo
            </TabsTrigger>
            <TabsTrigger value="settings">
              <Settings className="w-4 h-4 mr-2" />
              Configurações
            </TabsTrigger>
          </TabsList>

          {/* Leads Tab */}
          <TabsContent value="leads" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <Users className="w-8 h-8 text-blue-600" />
                    <div className="ml-4">
                      <p className="text-2xl font-bold">{leads.length}</p>
                      <p className="text-gray-600">Total Leads</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <DollarSign className="w-8 h-8 text-green-600" />
                    <div className="ml-4">
                      <p className="text-2xl font-bold">{leads.filter(l => l.status === 'fechado').length}</p>
                      <p className="text-gray-600">Fechados</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <Mail className="w-8 h-8 text-orange-600" />
                    <div className="ml-4">
                      <p className="text-2xl font-bold">{leads.filter(l => l.status === 'novo').length}</p>
                      <p className="text-gray-600">Novos</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <Phone className="w-8 h-8 text-purple-600" />
                    <div className="ml-4">
                      <p className="text-2xl font-bold">{leads.filter(l => l.status === 'demo_agendada').length}</p>
                      <p className="text-gray-600">Demos</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Lista de Leads</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {leads.map((lead) => (
                    <Card key={lead.id} className="border border-gray-200">
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <h3 className="font-semibold text-lg">{lead.name}</h3>
                                <p className="text-gray-600 flex items-center">
                                  <Building className="w-4 h-4 mr-1" />
                                  {lead.business_name}
                                </p>
                                <p className="text-gray-600 flex items-center">
                                  <Mail className="w-4 h-4 mr-1" />
                                  {lead.email}
                                </p>
                                <p className="text-gray-600 flex items-center">
                                  <Phone className="w-4 h-4 mr-1" />
                                  {lead.phone}
                                </p>
                              </div>
                              
                              <div>
                                <p className="text-sm text-gray-500 mb-1">Faturamento:</p>
                                <p className="font-medium">{lead.current_revenue || 'Não informado'}</p>
                                <p className="text-sm text-gray-500 mb-1 mt-2">Agendamentos/semana:</p>
                                <p className="font-medium">{lead.appointments_per_week || 'Não informado'}</p>
                              </div>
                              
                              <div>
                                <p className="text-sm text-gray-500 mb-1">Desafios:</p>
                                <p className="text-sm">{lead.main_challenges || 'Não informado'}</p>
                                <p className="text-sm text-gray-500 mt-2">Cadastrado em:</p>
                                <p className="text-sm">{formatDate(lead.timestamp)}</p>
                              </div>
                            </div>
                          </div>
                          
                          <div className="ml-4 flex flex-col items-end">
                            <Badge className={getStatusColor(lead.status)}>
                              {lead.status.replace('_', ' ')}
                            </Badge>
                            
                            <Select 
                              onValueChange={(value) => updateLeadStatus(lead.id, value)}
                              defaultValue={lead.status}
                            >
                              <SelectTrigger className="w-40 mt-2">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="novo">Novo</SelectItem>
                                <SelectItem value="contatado">Contatado</SelectItem>
                                <SelectItem value="demo_agendada">Demo Agendada</SelectItem>
                                <SelectItem value="fechado">Fechado</SelectItem>
                                <SelectItem value="negado">Negado</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  
                  {leads.length === 0 && (
                    <Card>
                      <CardContent className="p-8 text-center">
                        <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500">Nenhum lead cadastrado ainda</p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <Eye className="w-8 h-8 text-purple-600" />
                    <div className="ml-4">
                      <p className="text-2xl font-bold">{visits.length}</p>
                      <p className="text-gray-600">Total de Visitas</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <Globe className="w-8 h-8 text-blue-600" />
                    <div className="ml-4">
                      <p className="text-2xl font-bold">
                        {new Set(visits.map(v => v.country)).size}
                      </p>
                      <p className="text-gray-600">Países</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <MapPin className="w-8 h-8 text-green-600" />
                    <div className="ml-4">
                      <p className="text-2xl font-bold">
                        {new Set(visits.map(v => v.city)).size}
                      </p>
                      <p className="text-gray-600">Cidades</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Histórico de Visitas</span>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={fetchVisits}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Atualizar
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-4 text-xs text-gray-500 leading-relaxed">
                  <strong className="text-gray-700">Localização (cidade/estado)</strong> vem da base de geolocalização
                  por <strong>IP</strong> na Cloudflare — é o local típico do teu operador / relay (CGNAT, rede móvel,
                  iCloud Private Relay, VPN), não GPS. Pode diferir de Santos ou de outra cidade real. A linha
                  <strong className="text-gray-700"> Fuso (navegador)</strong> é só o fuso/língua do aparelho, para
                  contexto.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data/Hora</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Localização</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ISP</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Navegador</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Origem</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {visits.map((visit) => (
                        <tr key={visit.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {formatDate(visitInstant(visit))}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 font-mono">
                            {visit.ip_address}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            <div className="flex items-center">
                              <MapPin className="w-3 h-3 mr-1 text-gray-400" />
                              <div className="flex flex-col gap-0.5">
                                {[visit.city, visit.state, visit.country].filter(Boolean).length ? (
                                  <span>{[visit.city, visit.state, visit.country].filter(Boolean).join(", ")}</span>
                                ) : (
                                  <span className="text-gray-400">Não disponível (IP)</span>
                                )}
                                {(visit.client_timezone || visit.client_locale) && (
                                  <span className="text-[11px] text-gray-500">
                                    Fuso (navegador): {visit.client_timezone || "—"}
                                    {visit.client_locale ? ` · ${visit.client_locale}` : ""}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            <div className="flex items-center">
                              <Wifi className="w-3 h-3 mr-1 text-gray-400" />
                              {visit.isp || <span className="text-gray-400">N/A</span>}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                            {visit.user_agent ? (
                              <span title={visit.user_agent}>
                                {visit.user_agent.substring(0, 50)}...
                              </span>
                            ) : (
                              <span className="text-gray-400">N/A</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {visit.referrer ? (
                              <a 
                                href={visit.referrer} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline"
                              >
                                Ver origem
                              </a>
                            ) : (
                              <span className="text-gray-400">Direto</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {visits.length === 0 && (
                    <div className="p-8 text-center">
                      <Eye className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">Nenhuma visita registrada ainda</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Insights Tab */}
          <TabsContent value="insights" className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
              <Card className="bg-gradient-to-br from-purple-500 to-pink-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-100 text-sm">Usuários Ativos</p>
                      <p className="text-3xl font-bold">{getActiveUsers()}</p>
                      <p className="text-purple-200 text-xs mt-1">Últimos 5 minutos</p>
                    </div>
                    <Activity className="w-12 h-12 text-purple-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-blue-500 to-cyan-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm">Total de Visitas</p>
                      <p className="text-3xl font-bold">{visits.length}</p>
                      <p className="text-blue-200 text-xs mt-1">Desde o início</p>
                    </div>
                    <Eye className="w-12 h-12 text-blue-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-100 text-sm">Taxa de Conversão</p>
                      <p className="text-3xl font-bold">{getConversionRate()}%</p>
                      <p className="text-green-200 text-xs mt-1">Visitas → Leads</p>
                    </div>
                    <TrendingUp className="w-12 h-12 text-green-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-orange-500 to-red-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-orange-100 text-sm">Leads Gerados</p>
                      <p className="text-3xl font-bold">{leads.length}</p>
                      <p className="text-orange-200 text-xs mt-1">Total acumulado</p>
                    </div>
                    <Users className="w-12 h-12 text-orange-200" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Geographic Insights */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Globe className="w-5 h-5 mr-2 text-blue-600" />
                    Top 5 Países
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {getTopItems('country', 5).map((item, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Badge variant="outline" className="mr-2">#{index + 1}</Badge>
                          <span className="text-sm font-medium">{item.name}</span>
                        </div>
                        <Badge className="bg-blue-100 text-blue-800">{item.count} visitas</Badge>
                      </div>
                    ))}
                    {getTopItems('country', 5).length === 0 && (
                      <p className="text-gray-500 text-sm">Sem dados disponíveis</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <MapPin className="w-5 h-5 mr-2 text-green-600" />
                    Top 5 Estados
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {getTopItems('state', 5).map((item, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Badge variant="outline" className="mr-2">#{index + 1}</Badge>
                          <span className="text-sm font-medium">{item.name}</span>
                        </div>
                        <Badge className="bg-green-100 text-green-800">{item.count} visitas</Badge>
                      </div>
                    ))}
                    {getTopItems('state', 5).length === 0 && (
                      <p className="text-gray-500 text-sm">Sem dados disponíveis</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Building className="w-5 h-5 mr-2 text-purple-600" />
                    Top 5 Cidades
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {getTopItems('city', 5).map((item, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Badge variant="outline" className="mr-2">#{index + 1}</Badge>
                          <span className="text-sm font-medium">{item.name}</span>
                        </div>
                        <Badge className="bg-purple-100 text-purple-800">{item.count} visitas</Badge>
                      </div>
                    ))}
                    {getTopItems('city', 5).length === 0 && (
                      <p className="text-gray-500 text-sm">Sem dados disponíveis</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Time-based Analytics */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Clock className="w-5 h-5 mr-2 text-orange-600" />
                    Top 5 Horários do Dia
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {getTopHours().map((item, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Badge variant="outline" className="mr-2">#{index + 1}</Badge>
                          <span className="text-sm font-medium">{item.label}</span>
                        </div>
                        <div className="flex items-center">
                          <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                            <div 
                              className="bg-orange-500 h-2 rounded-full" 
                              style={{ width: `${(item.count / Math.max(...getTopHours().map(h => h.count))) * 100}%` }}
                            ></div>
                          </div>
                          <Badge className="bg-orange-100 text-orange-800">{item.count}</Badge>
                        </div>
                      </div>
                    ))}
                    {getTopHours().length === 0 && (
                      <p className="text-gray-500 text-sm">Sem dados disponíveis</p>
                    )}
                  </div>
                  <div className="mt-4 p-3 bg-orange-50 rounded-lg">
                    <p className="text-sm text-orange-800">
                      💡 <strong>Insight:</strong> Agende suas campanhas nos horários de pico para maior alcance!
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Calendar className="w-5 h-5 mr-2 text-indigo-600" />
                    Distribuição por Dia da Semana
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {getDayOfWeekDistribution()
                      .sort((a, b) => b.count - a.count)
                      .map((item, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <div className="flex items-center">
                            <Badge variant="outline" className="mr-2">#{index + 1}</Badge>
                            <span className="text-sm font-medium">{item.day}</span>
                          </div>
                          <div className="flex items-center">
                            <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                              <div 
                                className="bg-indigo-500 h-2 rounded-full" 
                                style={{ width: `${(item.count / Math.max(...getDayOfWeekDistribution().map(d => d.count))) * 100}%` }}
                              ></div>
                            </div>
                            <Badge className="bg-indigo-100 text-indigo-800">{item.count}</Badge>
                          </div>
                        </div>
                      ))}
                  </div>
                  <div className="mt-4 p-3 bg-indigo-50 rounded-lg">
                    <p className="text-sm text-indigo-800">
                      💡 <strong>Insight:</strong> Foque suas estratégias nos dias com maior tráfego!
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* ISP and Referrer Insights */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Wifi className="w-5 h-5 mr-2 text-cyan-600" />
                    Top 5 Provedores (ISP)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {getTopItems('isp', 5).map((item, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Badge variant="outline" className="mr-2">#{index + 1}</Badge>
                          <span className="text-sm font-medium truncate max-w-xs">{item.name}</span>
                        </div>
                        <Badge className="bg-cyan-100 text-cyan-800">{item.count}</Badge>
                      </div>
                    ))}
                    {getTopItems('isp', 5).length === 0 && (
                      <p className="text-gray-500 text-sm">Sem dados disponíveis</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <PieChart className="w-5 h-5 mr-2 text-pink-600" />
                    Fontes de Tráfego
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(() => {
                      const directCount = visits.filter(v => !v.referrer || v.referrer === 'null').length;
                      const referrers = getTopItems('referrer', 4);
                      const results = [
                        { name: 'Direto', count: directCount },
                        ...referrers
                      ].sort((a, b) => b.count - a.count).slice(0, 5);
                      
                      return results.map((item, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <div className="flex items-center">
                            <Badge variant="outline" className="mr-2">#{index + 1}</Badge>
                            <span className="text-sm font-medium truncate max-w-xs">
                              {item.name === 'Direto' ? '🔗 Direto' : `🌐 ${item.name.substring(0, 30)}...`}
                            </span>
                          </div>
                          <Badge className="bg-pink-100 text-pink-800">{item.count}</Badge>
                        </div>
                      ));
                    })()}
                  </div>
                  <div className="mt-4 p-3 bg-pink-50 rounded-lg">
                    <p className="text-sm text-pink-800">
                      💡 <strong>Insight:</strong> Tráfego direto indica reconhecimento de marca. Invista em SEO para outras fontes!
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Pricing Tab */}
          <TabsContent value="pricing" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <DollarSign className="w-5 h-5 mr-2 text-green-600" />
                  Controle de Preços e Descontos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  
                  {/* Planos Base */}
                  <Card className="bg-blue-50">
                    <CardHeader>
                      <CardTitle className="text-lg">📦 Planos Base (Sistema)</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-xs">Starter - Regular</Label>
                          <Input
                            type="number"
                            value={pricingSettings.starter_regular}
                            onChange={(e) => setPricingSettings({...pricingSettings, starter_regular: parseInt(e.target.value)})}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">Starter - Promo</Label>
                          <Input
                            type="number"
                            value={pricingSettings.starter_promo}
                            onChange={(e) => setPricingSettings({...pricingSettings, starter_promo: parseInt(e.target.value)})}
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label className="text-xs">Essencial - Regular</Label>
                          <Input
                            type="number"
                            value={pricingSettings.essencial_regular}
                            onChange={(e) => setPricingSettings({...pricingSettings, essencial_regular: parseInt(e.target.value)})}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">Essencial - Promo</Label>
                          <Input
                            type="number"
                            value={pricingSettings.essencial_promo}
                            onChange={(e) => setPricingSettings({...pricingSettings, essencial_promo: parseInt(e.target.value)})}
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label className="text-xs">Professional - Regular</Label>
                          <Input
                            type="number"
                            value={pricingSettings.professional_regular}
                            onChange={(e) => setPricingSettings({...pricingSettings, professional_regular: parseInt(e.target.value)})}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">Professional - Promo</Label>
                          <Input
                            type="number"
                            value={pricingSettings.professional_promo}
                            onChange={(e) => setPricingSettings({...pricingSettings, professional_promo: parseInt(e.target.value)})}
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label className="text-xs">Business - Regular</Label>
                          <Input
                            type="number"
                            value={pricingSettings.business_regular}
                            onChange={(e) => setPricingSettings({...pricingSettings, business_regular: parseInt(e.target.value)})}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">Business - Promo</Label>
                          <Input
                            type="number"
                            value={pricingSettings.business_promo}
                            onChange={(e) => setPricingSettings({...pricingSettings, business_promo: parseInt(e.target.value)})}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Baias e Setup */}
                  <Card className="bg-purple-50">
                    <CardHeader>
                      <CardTitle className="text-lg">🔧 Extras e Implementação</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-xs">Baia Extra - Regular</Label>
                          <Input
                            type="number"
                            value={pricingSettings.extra_bay_regular}
                            onChange={(e) => setPricingSettings({...pricingSettings, extra_bay_regular: parseInt(e.target.value)})}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">Baia Extra - Promo</Label>
                          <Input
                            type="number"
                            value={pricingSettings.extra_bay_promo}
                            onChange={(e) => setPricingSettings({...pricingSettings, extra_bay_promo: parseInt(e.target.value)})}
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label className="text-xs">Setup - Regular</Label>
                          <Input
                            type="number"
                            value={pricingSettings.setup_regular}
                            onChange={(e) => setPricingSettings({...pricingSettings, setup_regular: parseInt(e.target.value)})}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">Setup - Promo</Label>
                          <Input
                            type="number"
                            value={pricingSettings.setup_promo}
                            onChange={(e) => setPricingSettings({...pricingSettings, setup_promo: parseInt(e.target.value)})}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* 7Rosie IA */}
                  <Card className="bg-pink-50">
                    <CardHeader>
                      <CardTitle className="text-lg">🤖 7Rosie IA</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-xs">Implementação - Regular</Label>
                          <Input
                            type="number"
                            value={pricingSettings.rosie_implementation_regular}
                            onChange={(e) => setPricingSettings({...pricingSettings, rosie_implementation_regular: parseInt(e.target.value)})}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">Implementação - Promo</Label>
                          <Input
                            type="number"
                            value={pricingSettings.rosie_implementation_promo}
                            onChange={(e) => setPricingSettings({...pricingSettings, rosie_implementation_promo: parseInt(e.target.value)})}
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label className="text-xs">Mensalidade - Regular</Label>
                          <Input
                            type="number"
                            value={pricingSettings.rosie_monthly_regular}
                            onChange={(e) => setPricingSettings({...pricingSettings, rosie_monthly_regular: parseInt(e.target.value)})}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">Mensalidade - Promo</Label>
                          <Input
                            type="number"
                            value={pricingSettings.rosie_monthly_promo}
                            onChange={(e) => setPricingSettings({...pricingSettings, rosie_monthly_promo: parseInt(e.target.value)})}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Desconto Global */}
                  <Card className="bg-green-50">
                    <CardHeader>
                      <CardTitle className="text-lg">💰 Desconto Global</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <Label>Percentual de Desconto (%)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={pricingSettings.discount_percentage}
                          onChange={(e) => setPricingSettings({...pricingSettings, discount_percentage: parseInt(e.target.value)})}
                        />
                        <p className="text-xs text-gray-500">
                          Este percentual é exibido nas badges de desconto da landing page
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                  
                </div>
                
                <div className="mt-6">
                  <Button onClick={savePricingSettings} className="w-full" size="lg">
                    <Save className="w-4 h-4 mr-2" />
                    Salvar Todos os Preços
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Content Management Tab */}
          <TabsContent value="content" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Hero Section */}
              <Card>
                <CardHeader>
                  <CardTitle>Seção Hero</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Headline Principal</Label>
                    <Textarea
                      value={contentData.hero.headline}
                      onChange={(e) => setContentData(prev => ({
                        ...prev,
                        hero: { ...prev.hero, headline: e.target.value }
                      }))}
                      placeholder="Seu Banho e Tosa Fatura 40% Mais..."
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Sub-headline</Label>
                    <Input
                      value={contentData.hero.subheadline}
                      onChange={(e) => setContentData(prev => ({
                        ...prev,
                        hero: { ...prev.hero, subheadline: e.target.value }
                      }))}
                      placeholder="Como? Com uma Atendente de IA..."
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Descrição</Label>
                    <Textarea
                      value={contentData.hero.description}
                      onChange={(e) => setContentData(prev => ({
                        ...prev,
                        hero: { ...prev.hero, description: e.target.value }
                      }))}
                      placeholder="Sistema completo de gestão..."
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>CTA Principal</Label>
                    <Input
                      value={contentData.hero.ctaPrimary}
                      onChange={(e) => setContentData(prev => ({
                        ...prev,
                        hero: { ...prev.hero, ctaPrimary: e.target.value }
                      }))}
                      placeholder="QUERO SER UM DOS 10 PRIMEIROS..."
                    />
                  </div>
                  
                  <Button onClick={() => saveContent('hero')} className="w-full">
                    <Save className="w-4 h-4 mr-2" />
                    Salvar Hero
                  </Button>
                </CardContent>
              </Card>

              {/* Pricing Section */}
              <Card>
                <CardHeader>
                  <CardTitle>Preços</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Preço Regular</Label>
                      <Input
                        type="number"
                        value={contentData.pricing.regularPrice}
                        onChange={(e) => setContentData(prev => ({
                          ...prev,
                          pricing: { ...prev.pricing, regularPrice: parseInt(e.target.value) }
                        }))}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Preço Promocional</Label>
                      <Input
                        type="number"
                        value={contentData.pricing.promoPrice}
                        onChange={(e) => setContentData(prev => ({
                          ...prev,
                          pricing: { ...prev.pricing, promoPrice: parseInt(e.target.value) }
                        }))}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Setup Regular</Label>
                      <Input
                        type="number"
                        value={contentData.pricing.setupRegular}
                        onChange={(e) => setContentData(prev => ({
                          ...prev,
                          pricing: { ...prev.pricing, setupRegular: parseInt(e.target.value) }
                        }))}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Setup Promocional</Label>
                      <Input
                        type="number"
                        value={contentData.pricing.setupPromo}
                        onChange={(e) => setContentData(prev => ({
                          ...prev,
                          pricing: { ...prev.pricing, setupPromo: parseInt(e.target.value) }
                        }))}
                      />
                    </div>
                  </div>
                  
                  <Button onClick={() => saveContent('pricing')} className="w-full">
                    <Save className="w-4 h-4 mr-2" />
                    Salvar Preços
                  </Button>
                </CardContent>
              </Card>

              {/* Countdown Timer */}
              <Card>
                <CardHeader>
                  <CardTitle>Timer da Promoção</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Data de Expiração da Promoção</Label>
                    <Input
                      type="datetime-local"
                      value={contentData.countdown.expirationDate || ''}
                      onChange={(e) => setContentData(prev => ({
                        ...prev,
                        countdown: { expirationDate: e.target.value }
                      }))}
                    />
                    <p className="text-sm text-gray-500">
                      Defina quando a promoção expira. O timer será calculado automaticamente.
                    </p>
                  </div>
                  
                  <Button onClick={() => saveCountdown()} className="w-full">
                    <Save className="w-4 h-4 mr-2" />
                    Salvar Timer
                  </Button>
                </CardContent>
              </Card>

              {/* Available Slots */}
              <Card>
                <CardHeader>
                  <CardTitle>Gestão de Vagas da Promoção</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Total de Vagas</Label>
                      <Input
                        type="number"
                        min="1"
                        value={contentData.slots.totalSlots || 10}
                        onChange={(e) => setContentData(prev => ({
                          ...prev,
                          slots: { 
                            ...prev.slots,
                            totalSlots: e.target.value 
                          }
                        }))}
                      />
                      <p className="text-xs text-gray-500">
                        Quantidade total de vagas
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Vagas Disponíveis</Label>
                      <Input
                        type="number"
                        min="0"
                        value={contentData.slots.availableSlots || 7}
                        onChange={(e) => setContentData(prev => ({
                          ...prev,
                          slots: { 
                            ...prev.slots,
                            availableSlots: e.target.value 
                          }
                        }))}
                      />
                      <p className="text-xs text-gray-500">
                        Vagas ainda disponíveis
                      </p>
                    </div>
                  </div>
                  
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-blue-700 font-medium">Vagas Preenchidas:</span>
                      <span className="text-blue-900 font-bold">
                        {(contentData.slots.totalSlots || 10) - (contentData.slots.availableSlots || 7)}
                      </span>
                    </div>
                    <div className="mt-2 w-full bg-blue-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                        style={{ 
                          width: `${((contentData.slots.totalSlots - contentData.slots.availableSlots) / contentData.slots.totalSlots * 100) || 0}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                  
                  <Button onClick={() => saveSlots()} className="w-full">
                    <Save className="w-4 h-4 mr-2" />
                    Salvar Vagas
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Site Institucional Tab */}
          <TabsContent value="institutional" className="space-y-6">
            <Card className="border-2 border-blue-400 bg-gradient-to-br from-slate-50 to-blue-50">
              <CardHeader>
                <CardTitle className="flex items-center text-blue-600">
                  <span className="text-3xl mr-3">🏢</span>
                  Site Institucional BobaPet
                </CardTitle>
                <p className="text-gray-700 text-sm mt-2">
                  Ative o site institucional premium - página profissional para apresentar o BobaPet sem promoções
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Status e Ativação */}
                  <div className="p-4 bg-blue-100 border-2 border-blue-400 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-bold text-blue-600 text-lg mb-1">Status do Site Institucional</h4>
                        <p className="text-gray-700 text-sm">
                          {institutionalSettings.is_active 
                            ? '🟢 Ativo - Landing page exibindo site institucional premium' 
                            : '🔴 Inativo - Usando promoção normal ou lista de espera'}
                        </p>
                      </div>
                      <Button
                        onClick={async () => {
                          const newActiveState = !institutionalSettings.is_active;
                          setInstitutionalSettings(prev => ({ ...prev, is_active: newActiveState }));
                          
                          try {
                            const updatePayload = {
                              is_active: newActiveState,
                              whatsapp_number: institutionalSettings.whatsapp_number,
                              base_price: institutionalSettings.base_price,
                            };
                            
                            const response = await fetch(`${BACKEND_URL}/api/institutional/settings`, {
                              method: 'PUT',
                              headers: {
                                'Content-Type': 'application/json',
                              },
                              body: JSON.stringify(updatePayload),
                            });
                            
                            if (response.ok) {
                              alert(`🏢 Site Institucional ${newActiveState ? 'ativado' : 'desativado'} com sucesso!`);
                              fetchInstitutionalSettings();
                            } else {
                              const errorData = await response.text();
                              console.error('Error response:', errorData);
                              alert('Erro ao atualizar status. Tente novamente.');
                              setInstitutionalSettings(prev => ({ ...prev, is_active: !newActiveState }));
                            }
                          } catch (error) {
                            console.error('Error toggling Institutional mode:', error);
                            alert('Erro ao atualizar status. Tente novamente.');
                            setInstitutionalSettings(prev => ({ ...prev, is_active: !newActiveState }));
                          }
                        }}
                        className={`${
                          institutionalSettings.is_active 
                            ? 'bg-red-600 hover:bg-red-700' 
                            : 'bg-blue-600 hover:bg-blue-700'
                        } text-white font-bold text-lg px-8 py-6`}
                      >
                        {institutionalSettings.is_active ? 'Desativar' : 'Ativar'} Site Institucional
                      </Button>
                    </div>
                  </div>

                  {/* Configurações */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">⚙️ Configurações</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* WhatsApp */}
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          WhatsApp (Freddy - IA)
                        </label>
                        <input
                          type="text"
                          value={institutionalSettings.whatsapp_number}
                          onChange={(e) => setInstitutionalSettings(prev => ({ ...prev, whatsapp_number: e.target.value }))}
                          className="w-full px-4 py-2 border rounded-lg"
                          placeholder="5513996265777"
                        />
                        <p className="text-xs text-gray-500 mt-1">Número para contato via WhatsApp (formato: 5513996265777)</p>
                      </div>

                      {/* Preço Base */}
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Preço Base (Sistema até 10 atend/dia)
                        </label>
                        <input
                          type="number"
                          value={institutionalSettings.base_price}
                          onChange={(e) => setInstitutionalSettings(prev => ({ ...prev, base_price: parseInt(e.target.value) }))}
                          className="w-full px-4 py-2 border rounded-lg"
                          placeholder="99"
                        />
                        <p className="text-xs text-gray-500 mt-1">Valor em R$ para o plano básico (sistema sem IA)</p>
                      </div>

                      <Button 
                        onClick={saveInstitutionalSettings} 
                        className="w-full bg-blue-600 hover:bg-blue-700"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Salvar Configurações
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Info sobre prioridade */}
                  <Card className="bg-purple-50 border-2 border-purple-300">
                    <CardContent className="p-4">
                      <h4 className="font-bold text-purple-700 mb-2">📌 Prioridade de Exibição</h4>
                      <p className="text-sm text-gray-700">
                        <strong>1º Site Institucional</strong> (quando ativo) → 
                        <strong> 2º Promoção Normal</strong> (countdown ativo) → 
                        <strong> 3º Lista de espera</strong>
                      </p>
                      <p className="text-xs text-gray-600 mt-2">
                        💡 O site institucional tem a maior prioridade. Quando ativo, sobrepõe qualquer outro modo.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            {/* Backup/Restore Card */}
            <Card className="border-2 border-orange-200 bg-orange-50">
              <CardHeader>
                <CardTitle className="flex items-center text-orange-800">
                  <Database className="w-5 h-5 mr-2" />
                  Backup e Restauração de Dados
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 bg-orange-100 border border-orange-300 rounded-lg">
                    <h4 className="font-semibold text-orange-900 mb-2">⚠️ Importante:</h4>
                    <p className="text-orange-800 text-sm">
                      Faça backup regularmente! Todos os dados (leads, visitas, configurações) serão perdidos após deploy se você não tiver backup.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <h4 className="font-semibold text-gray-800">💾 Baixar Backup</h4>
                      <p className="text-sm text-gray-600 mb-2">
                        Salva todos os dados em um arquivo JSON. Faça isso ANTES de cada deploy!
                      </p>
                      <Button 
                        onClick={downloadBackup} 
                        className="w-full bg-green-600 hover:bg-green-700"
                        size="lg"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Baixar Backup Agora
                      </Button>
                    </div>

                    <div className="space-y-2">
                      <h4 className="font-semibold text-gray-800">📤 Restaurar Backup</h4>
                      <p className="text-sm text-gray-600 mb-2">
                        Carrega dados de um backup anterior. Use DEPOIS do deploy.
                      </p>
                      <label htmlFor="restore-file">
                        <Button 
                          as="span"
                          className="w-full bg-blue-600 hover:bg-blue-700"
                          size="lg"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Restaurar de Backup
                        </Button>
                      </label>
                      <input
                        id="restore-file"
                        type="file"
                        accept=".json"
                        onChange={restoreBackup}
                        className="hidden"
                      />
                    </div>
                  </div>

                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      💡 <strong>Dica:</strong> Baixe um backup antes de cada deploy e guarde em local seguro. Após o deploy, use "Restaurar de Backup" para recuperar seus dados.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Configurações Gerais</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-semibold text-blue-800 mb-2">Como acessar a área admin:</h4>
                    <p className="text-blue-700">
                      🔗 URL: <code className="bg-blue-100 px-2 py-1 rounded">{ADMIN_PATH}</code>
                    </p>
                    <p className="text-blue-700 mt-1">
                      📱 WhatsApp configurado: <strong>(13) 99626-5777</strong>
                    </p>
                  </div>
                  
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <h4 className="font-semibold text-green-800 mb-2">Funcionalidades ativas:</h4>
                    <ul className="text-green-700 space-y-1">
                      <li>✅ Formulário salva leads no banco</li>
                      <li>✅ Área admin funcional</li>
                      <li>✅ Edição de conteúdo em tempo real</li>
                      <li>✅ Gerenciamento de status de leads</li>
                      <li>✅ Timer de contagem regressiva configurável</li>
                      <li>✅ Sistema de backup e restauração</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};